// Central API service for all backend communications

// OLD: Localhost configuration (commented out)
// const API_BASE_URL = 'http://localhost:5000/api';

// NEW: Production configuration with backend subdomain
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
                     (window.location.origin.includes('localhost') ? 
                     'http://localhost:5000/api' : 
                     'https://backend.aswcars.com/api');

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Auto-append CRM Session Token if available (prioritize tab-isolated sessionStorage)
    const crmToken = sessionStorage.getItem('aswCrmToken') || localStorage.getItem('aswCrmToken');
    const authHeader = crmToken ? { 'Authorization': `Bearer ${crmToken}` } : {};

    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
        ...options.headers,
      },
    };

    const requestOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    try {
      console.log(`Making API request to: ${url}`, requestOptions);
      
      const response = await fetch(url, requestOptions);
      
      console.log(`API Response Status: ${response.status}`);
      
      if (response.status === 401 || response.status === 403) {
        // Clear tab and global session tokens
        sessionStorage.removeItem('aswCrmToken');
        sessionStorage.removeItem('aswCrmUser');
        sessionStorage.removeItem('aswSessionToken');
        sessionStorage.removeItem('aswUser');
        localStorage.removeItem('aswCrmToken');
        localStorage.removeItem('aswCrmUser');
        localStorage.removeItem('aswSessionToken');
        localStorage.removeItem('aswUser');

        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/crm') {
          if (currentPath.includes('/crm')) {
            window.location.href = '/crm';
          } else if (currentPath.includes('/dashboard')) {
            window.location.href = '/login';
          }
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response Data:', data);
      
      return data;
    } catch (error) {
      console.error('API Request Error:', error);
      
      // Enhanced error handling
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Backend server is not accessible. Please ensure the backend is running on backend.aswcars.com.');
      } else if (error.message.includes('status: 404')) {
        throw new Error('API endpoint not found. Please check the backend server configuration.');
      } else if (error.message.includes('status: 500')) {
        throw new Error('Internal server error. Please check the backend logs.');
      } else {
        throw error;
      }
    }
  }

  // Authentication methods
  async login(email, password) {
    return this.makeRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async verifySession(sessionToken, user) {
    return this.makeRequest('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ sessionToken, user }),
    });
  }

  async logout(userId) {
    return this.makeRequest('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
  }

  // --- CRM Methods ---
  
  // Users
  async getCrmUsers() {
    return this.makeRequest('/crm/users');
  }
  
  async createCrmUser(userData) {
    return this.makeRequest('/crm/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  async updateCrmUser(id, userData) {
    return this.makeRequest(`/crm/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  async deleteCrmUser(id) {
    return this.makeRequest(`/crm/users/${id}`, {
      method: 'DELETE'
    });
  }

  // Branches
  async getCrmBranches() {
    return this.makeRequest('/crm/branches');
  }

  async createCrmBranch(branchData) {
    return this.makeRequest('/crm/branches', {
      method: 'POST',
      body: JSON.stringify(branchData)
    });
  }

  async updateCrmBranch(id, branchData) {
    return this.makeRequest(`/crm/branches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(branchData)
    });
  }

  async deleteCrmBranch(id) {
    return this.makeRequest(`/crm/branches/${id}`, {
      method: 'DELETE'
    });
  }

  // Leads
  async getCrmLeads(params = {}) {
    const query = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        query.append(key, params[key]);
      }
    });
    return this.makeRequest(`/crm/leads?${query.toString()}`);
  }

  async getOverviewStats() {
    return this.makeRequest('/crm/leads/dashboard-stats');
  }

  async createCrmLead(leadData) {
    return this.makeRequest('/crm/leads', {
      method: 'POST',
      body: JSON.stringify(leadData)
    });
  }

  async submitPublicChatbotLead(leadData) {
    return this.makeRequest('/crm/leads/public', {
      method: 'POST',
      body: JSON.stringify(leadData)
    });
  }

  async updateCrmLead(id, updateData) {
    return this.makeRequest(`/crm/leads/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  async deleteCrmLead(id, performedBy) {
    const q = performedBy ? `?performed_by=${encodeURIComponent(performedBy)}` : '';
    return this.makeRequest(`/crm/leads/${id}${q}`, {
      method: 'DELETE'
    });
  }

  async syncCrmFacebookLeads() {
    return this.makeRequest('/crm/leads/sync-facebook', {
      method: 'POST'
    });
  }



  // Customers & Workflows
  async getCrmCustomers(params) {
    let q = '';
    if (typeof params === 'string') {
      q = `?workflow_step=${encodeURIComponent(params)}`;
    } else if (params && typeof params === 'object') {
      const searchParams = new URLSearchParams();
      if (params.type) searchParams.append('type', params.type);
      if (params.workflow_step) searchParams.append('workflow_step', params.workflow_step);
      if (params.branch_id) searchParams.append('branch_id', params.branch_id);
      q = `?${searchParams.toString()}`;
    }
    return this.makeRequest(`/crm/customers${q}`);
  }

  async createCrmCustomer(customerData) {
    return this.makeRequest('/crm/customers', {
      method: 'POST',
      body: JSON.stringify(customerData)
    });
  }

  async updateCrmCustomer(id, customerData) {
    return this.makeRequest(`/crm/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData)
    });
  }

  async deleteCrmCustomer(id) {
    return this.makeRequest(`/crm/customers/${id}`, {
      method: 'DELETE'
    });
  }

  async getCrmCustomerJourney(customerId) {
    return this.makeRequest(`/crm/customers/${customerId}/journey`);
  }

  // Roles
  async getCrmRoles() {
    return this.makeRequest('/crm/roles');
  }

  async createCrmRole(roleData) {
    return this.makeRequest('/crm/roles', {
      method: 'POST',
      body: JSON.stringify(roleData)
    });
  }

  async updateCrmWorkflowStep(id, updateData) {
    return this.makeRequest(`/crm/workflows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  // Activities & Logs
  async getCrmActivities(leadId) {
    return this.makeRequest(`/crm/activities?lead_id=${leadId}&entity_type=lead`);
  }

  async logCrmActivity(activityData) {
    return this.makeRequest('/crm/activities', {
      method: 'POST',
      body: JSON.stringify(activityData)
    });
  }

  async getAdminActivityLogs() {
    return this.makeRequest('/crm/activities/all');
  }

  // Notifications
  async getUnreadNotifications(userId) {
    return this.makeRequest(`/crm/notifications?user_id=${userId}`);
  }

  async markNotificationAsRead(id) {
    return this.makeRequest(`/crm/notifications/${id}/read`, { method: 'PUT' });
  }

  async markAllNotificationsAsRead(userId) {
    return this.makeRequest('/crm/notifications/read-all', {
      method: 'PUT',
      body: JSON.stringify({ user_id: userId })
    });
  }

  // Tasks
  async getCrmTasks(userId, role) {
    const params = userId ? `user_id=${userId}&role=${encodeURIComponent(role || '')}` : '';
    return this.makeRequest(`/crm/tasks?${params}`);
  }

  async getAllCrmTasks() {
    return this.makeRequest('/crm/tasks');
  }

  async createCrmTask(taskData) {
    return this.makeRequest('/crm/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData)
    });
  }

  async updateCrmTask(id, updateData) {
    return this.makeRequest(`/crm/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
  }

  async deleteCrmTask(id) {
    return this.makeRequest(`/crm/tasks/${id}`, { method: 'DELETE' });
  }

  // Announcements
  async getCrmAnnouncements() {
    return this.makeRequest('/crm/announcements');
  }

  async createCrmAnnouncement(data) {
    return this.makeRequest('/crm/announcements', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deactivateAnnouncement(id) {
    return this.makeRequest(`/crm/announcements/${id}/deactivate`, { method: 'PUT' });
  }

  async deleteAnnouncement(id) {
    return this.makeRequest(`/crm/announcements/${id}`, { method: 'DELETE' });
  }

  // Cars
  async getCrmCars() {
    return this.makeRequest('/crm/cars');
  }

  // Checklists
  async getStepChecklist(stepId) {
    return this.makeRequest(`/crm/workflows/steps/${stepId}/checklist`);
  }

  async addChecklistItem(stepId, taskName) {
    return this.makeRequest(`/crm/workflows/steps/${stepId}/checklist`, {
      method: 'POST',
      body: JSON.stringify({ task_name: taskName })
    });
  }

  async toggleChecklistItem(checklistId, isCompleted) {
    return this.makeRequest(`/crm/workflows/checklist/${checklistId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_completed: isCompleted })
    });
  }

  // Public Tracking
  async getPublicTrackingData(token) {
    return this.makeRequest(`/crm/customers/public/track/${token}`);
  }

  // Test method
  async testConnection() {
    return this.makeRequest('/test');
  }

  // Ping Activity Tracker
  async pingActivity(id) {
    if (!id || id === 'legacy-admin' || id === 't-permit-admin') return { success: true };
    return this.makeRequest(`/crm/users/${id}/ping`, { method: 'PUT' });
  }

  // Logout CRM
  async logoutCrm(userId) {
    if (!userId || userId === 'legacy-admin' || userId === 't-permit-admin') return { success: true };
    return this.makeRequest('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
  }

  // Lead Distribution System APIs
  async getLeadDistributionSettings() {
    return this.makeRequest('/crm/leads/distribution/settings');
  }

  async updateLeadDistributionSettings(value) {
    return this.makeRequest('/crm/leads/distribution/settings', {
      method: 'PUT',
      body: JSON.stringify({ value })
    });
  }

  async rebalanceLeads(managerId) {
    return this.makeRequest('/crm/leads/distribution/rebalance', {
      method: 'POST',
      body: JSON.stringify({ manager_id: managerId })
    });
  }

  async markLeadContacted(leadId, performedBy) {
    return this.makeRequest(`/crm/leads/${leadId}/contacted`, {
      method: 'PUT',
      body: JSON.stringify({ performed_by: performedBy })
    });
  }

  async getLeadDistributionStats() {
    return this.makeRequest('/crm/leads/distribution/stats');
  }

  // Showroom Bookings
  async getBookings(page = 1, limit = 20, status = '') {
    const q = new URLSearchParams();
    q.append('page', page);
    q.append('limit', limit);
    if (status) q.append('status', status);
    return this.makeRequest(`/bookings?${q.toString()}`);
  }

  async getBookingsSummary() {
    return this.makeRequest('/bookings/dashboard/summary');
  }
}

export default new ApiService(); 